import { Email } from '@mui/icons-material';
import { IconButton, Tooltip, Box } from '@mui/material';
import {
  faBracketsCurly,
  faBuildings,
  faComments,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { SUBMISSION_SOURCE } from '../../api/submissions/submissions.api.types';
import useNotifications from '../../_lib/notifications/useNotifications';
import { useCmailModal } from './CmailModalContext';

interface SubmissionSourceIconProps {
  submissionSource: SUBMISSION_SOURCE | string | undefined;
  msgId?: string | null;
  size?: 'small' | 'medium' | 'large';
}

export const SubmissionSourceIcon: React.FC<SubmissionSourceIconProps> = ({
  submissionSource,
  msgId,
  size = 'small',
}) => {
  const notifications = useNotifications();
  const cmailModal = useCmailModal();

  const handleCmailClick = async (event: React.MouseEvent) => {
    // Stop event propagation to prevent bubbling to parent accordions/popovers
    event.stopPropagation();
    event.preventDefault();

    if (submissionSource !== SUBMISSION_SOURCE.EMAIL) {
      notifications.warning(
        `Email link is only available for EMAIL submissions. This submission source is: ${submissionSource}`
      );
      return;
    }

    if (!msgId) {
      notifications.error(
        'Email link not available - missing message ID in submission metadata.'
      );
      return;
    }

    // Open modal via global context - modal is rendered outside this component's hierarchy
    await cmailModal.openCmailModal(msgId);
  };

  const getSourceDisplayName = (source: SUBMISSION_SOURCE): string => {
    switch (source) {
      case SUBMISSION_SOURCE.API:
        return 'API';
      case SUBMISSION_SOURCE.BROKER_PORTAL:
        return 'Broker Portal';
      case SUBMISSION_SOURCE.EMAIL:
        return 'Email';
      case SUBMISSION_SOURCE.OMNI:
        return 'Chat';
      default:
        return source;
    }
  };

  const getIconSize = () => {
    const sizes = {
      small: '1rem',
      medium: '1.5rem',
      large: '2rem',
    };
    return sizes[size];
  };

  const getBrokerPortalSize = () => {
    const sizes = {
      small: '0.875rem',
      medium: '1.25rem',
      large: '1.75rem',
    };
    return sizes[size];
  };

  const renderIcon = () => {
    const iconSize = getIconSize();
    const brokerSize = getBrokerPortalSize();
    const source = submissionSource as SUBMISSION_SOURCE;

    switch (source) {
      case SUBMISSION_SOURCE.API:
        return (
          <FontAwesomeIcon
            icon={faBracketsCurly}
            style={{ fontSize: iconSize, width: iconSize, height: iconSize }}
          />
        );
      case SUBMISSION_SOURCE.BROKER_PORTAL:
        return (
          <FontAwesomeIcon
            icon={faBuildings}
            style={{
              fontSize: brokerSize,
              width: brokerSize,
              height: brokerSize,
            }}
          />
        );
      case SUBMISSION_SOURCE.EMAIL:
        return <Email sx={{ fontSize: iconSize }} />;
      case SUBMISSION_SOURCE.OMNI:
        return (
          <FontAwesomeIcon
            icon={faComments}
            style={{ fontSize: iconSize, width: iconSize, height: iconSize }}
          />
        );
      default:
        return null;
    }
  };

  // Early return if submissionSource is invalid
  if (
    !submissionSource ||
    !Object.values(SUBMISSION_SOURCE).includes(
      submissionSource as SUBMISSION_SOURCE
    )
  ) {
    return null;
  }

  const tooltipText = `Submission Source: ${getSourceDisplayName(
    submissionSource as SUBMISSION_SOURCE
  )}`;

  const iconElement = renderIcon();

  if (!iconElement) {
    return null;
  }

  // For Email source, make it clickable
  const source = submissionSource as SUBMISSION_SOURCE;
  if (source === SUBMISSION_SOURCE.EMAIL) {
    return (
      <Tooltip title={tooltipText}>
        <IconButton
          onClick={handleCmailClick}
          sx={{
            padding: 0,
            color: '#2563eb',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          size={size}
        >
          {iconElement}
        </IconButton>
      </Tooltip>
    );
  }

  // For non-Email sources, show icon with tooltip (no box/border)
  return (
    <Tooltip title={tooltipText}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          cursor: 'default',
        }}
      >
        {iconElement}
      </Box>
    </Tooltip>
  );
};
